import React from 'react';
import { type Moment, type MomentInput } from 'moment';
import { addValidationRule, withFormsy } from 'formsy-react';
import { useIntl } from 'react-intl';
import { IconButton, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import BaseTextField, { type BaseTextFieldProps, type FieldProps } from './components/BaseTextField';
import withFormsyValidations from './components/WithFormsyValidations';
import { parseDate } from '../Utils';
import Icon from '../Icon';

const globalMinDate = '1753-01-01';

addValidationRule<string | null>('minDate', (_model, newValue, minValue) => {
	const parsedCurrentDate = parseDate(newValue);
	const parsedMinDate = parseDate(minValue);

	if (!parsedCurrentDate || !parsedMinDate) return true;
	return parsedCurrentDate?.isSameOrAfter(parsedMinDate);
});

addValidationRule<string | null>('maxDate', (_model, newValue, maxValue) => {
	const parsedCurrentDate = parseDate(newValue);
	const parsedMaxDate = parseDate(maxValue);

	if (!parsedCurrentDate || !parsedMaxDate) return true;
	return parsedCurrentDate?.isSameOrBefore(parsedMaxDate);
});

type DateFieldProps = Omit<FieldProps<string, Moment>, 'endAdornment'> & {
	shouldDisableDate?: (day: Moment) => boolean;
};

const DateTextField = styled(BaseTextField)<BaseTextFieldProps<string>>(({ isEdit, theme }) => ({
	'&.MuiTextField-root > .MuiInputBase-root': {
		paddingRight: isEdit ? 4 : 12
	}
}));

/** The date field, used to specify dates */
function DateField(props: DateFieldProps) {
	const intl = useIntl();
	const hasLogs = props.logProps?.hasLogs(props.name);

	const ref = React.useRef<HTMLDivElement>(null);

	const [dialogOpen, setDialogOpen] = React.useState(false);
	const switchDialogOpen = () => setDialogOpen(state => !state);

	const internalValue = React.useRef(parseDate(props.value));

	function fieldParser(fieldValue: MomentInput) {
		const parsedValue = parseDate(fieldValue);
		if (parsedValue !== null) return intl.formatDate(parsedValue?.toDate());
		return null;
	}

	function handleBlur() {
		const newValue = internalValue && internalValue.current?.isValid() ? internalValue.current?.utc() : undefined;
		if (props.beforeChange) props.beforeChange(newValue);
		props.sendChange(props.name, newValue?.toISOString() || null);
		if (props.afterChange) props.afterChange(newValue);
	}

	function handleChange(newValue: Moment | null) {
		internalValue.current = newValue?.startOf('day');
		if (internalValue.current?.isSameOrAfter(globalMinDate)) {
			props.setValue(internalValue.current?.toISOString() || null);
		}
	}

	function handleDialogClose() {
		handleBlur();
		setDialogOpen(false);
	}

	return (
		<DatePicker
			ref={ref}
			className={props.className}
			name={props.name}
			label={props.label}
			format="DD/MM/YYYY"
			formatDensity="dense"
			minDate={parseDate((props.validations as any)?.minDate)}
			maxDate={parseDate((props.validations as any)?.maxDate)}
			value={parseDate(props.value) ?? null}
			slots={{ textField: DateTextField }}
			slotProps={{
				textField: {
					name: props.name,
					isEdit: props.isEdit,
					startAdornment: props.startAdornment,
					InputProps: {
						endAdornment: props.isEdit ? (
							<IconButton size="small" onClick={switchDialogOpen}>
								<Icon icon="calendar" />
							</IconButton>
						) : (
							<></>
						)
					},
					required: props.required,
					errorMessage: props.errorMessage,
					isFormSubmitted: props.isFormSubmitted,
					isPristine: props.isPristine,
					isValid: props.isValid,
					logProps: props.logProps,
					fieldParser,
					onBlur: handleBlur
				} as BaseTextFieldProps<string>,
				popper: {
					anchorEl: ref?.current
				}
			}}
			timezone="system"
			readOnly={hasLogs}
			disabled={!props.isEdit && !hasLogs}
			onChange={handleChange}
			onAccept={handleBlur}
			open={dialogOpen}
			onClose={handleDialogClose}
			displayWeekNumber
			enableAccessibleFieldDOMStructure={false}
			shouldDisableDate={props.shouldDisableDate}
		/>
	);
}

export default React.memo(
	withFormsyValidations(withFormsy(DateField), {
		validations: { minDate: globalMinDate },
		validationErrors: ({ t, intl }) => ({
			minDate: intl.formatMessage(
				{ id: 'dateField.minDate', defaultMessage: t('ERROR_MIN_DATE') },
				{ value: intl.formatDate(parseDate(globalMinDate)?.toDate()) }
			)
		})
	})
);
